import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { ChevronDown } from 'lucide-react';
import usePermissions from '../hooks/usePermissions';

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const { permissions } = usePermissions();

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  // Check if user has at least one admin menu permission
  const hasAdminAccess = permissions.includes('ADMIN_GESTIONE_PERMESSI') || 
                         permissions.includes('ADMIN_PAGAMENTI') || 
                         permissions.includes('ADMIN_CATEGORIE_SISTEMA') ||
                         permissions.includes('ADMIN_SITUAZIONE_QUOTE') ||
                         permissions.includes('ADMIN_ULTIMI_ACCESSI') ||
                         permissions.includes('LETTURA_ESITO_VOTAZIONE');

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminMenuOpen && !event.target.closest('.admin-dropdown-container')) {
        setAdminMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [adminMenuOpen]);

  const linkClass = "px-3 py-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-50";
  const mobileLinkClass = "block py-3 text-lg font-medium hover:text-gray-600 transition-colors";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to={user ? "/dashboard" : "/"} className="text-xl font-bold text-gray-900">
              BASE
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden lg:flex items-center space-x-1">
            {!user ? (
              <>
                <Link to="/" className={linkClass} data-testid="home-link">Home</Link>
                <Link to="/statuto" className={linkClass} data-testid="statuto-link">Statuto</Link>
                <Link to="/regolamento" className={linkClass} data-testid="regolamento-link">Regolamento</Link>
                <Link to="/advisory-board" className={linkClass} data-testid="advisory-board-link">Advisory Board</Link>
                <Link to="/contatti" className={linkClass} data-testid="contatti-link">Contatti</Link>
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="border-2 border-black hover:bg-black hover:text-white transition-colors"
                    data-testid="login-button"
                  >
                    Login
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/dashboard" className={linkClass} data-testid="dashboard-link">Dashboard</Link>
                <Link to="/members" className={linkClass} data-testid="members-link">Membri</Link>
                <Link to="/member-news" className={linkClass} data-testid="member-news-link">Bacheca</Link>
                <Link to="/jobs" className={linkClass} data-testid="jobs-link">Opportunità</Link>
                <Link to="/activities" className={linkClass} data-testid="activities-link">Attività</Link>
                <Link to="/archive" className={linkClass} data-testid="archive-link">Archivio</Link>
                <Link to="/profile" className={linkClass} data-testid="profile-link">Profilo</Link>
                
                {/* Admin Dropdown Menu */}
                {hasAdminAccess && (
                  <div className="relative inline-block admin-dropdown-container">
                    <button
                      onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-900 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-50"
                    >
                      Admin
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    {adminMenuOpen && (
                      <div className="absolute right-0 mt-2 w-60 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                        {permissions.includes('ADMIN_GESTIONE_PERMESSI') && (
                          <Link
                            to="/admin/roles"
                            className="block px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            Gestione gruppi
                          </Link>
                        )}
                        {permissions.includes('ADMIN_CATEGORIE_SISTEMA') && (
                          <Link
                            to="/configurations"
                            className="block px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            Configurazioni di sistema
                          </Link>
                        )}
                        {permissions.includes('ADMIN_PAGAMENTI') && (
                          <Link
                            to="/admin/payments"
                            className="block px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            Pagamenti stripe
                          </Link>
                        )}
                        {permissions.includes('ADMIN_SITUAZIONE_QUOTE') && (
                          <Link
                            to="/admin/quote-situation"
                            className="block px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            Situazione quote
                          </Link>
                        )}
                        {permissions.includes('ADMIN_ULTIMI_ACCESSI') && (
                          <Link
                            to="/admin/recent-accesses"
                            className="block px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            Lista ultimi accessi
                          </Link>
                        )}
                        {permissions.includes('LETTURA_ESITO_VOTAZIONE') && (
                          <Link
                            to="/admin/voting-results"
                            className="block px-4 py-3 text-sm text-gray-900 hover:bg-gray-50"
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            Esiti votazioni
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="border-2 border-black hover:bg-black hover:text-white transition-colors"
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-64 overflow-y-auto overscroll-contain">
                <div className="py-4">
                  {!user ? (
                    <>
                      <Link to="/" className={mobileLinkClass} onClick={closeMobileMenu}>Home</Link>
                      <Link to="/statuto" className={mobileLinkClass} onClick={closeMobileMenu}>Statuto</Link>
                      <Link to="/regolamento" className={mobileLinkClass} onClick={closeMobileMenu}>Regolamento</Link>
                      <Link to="/advisory-board" className={mobileLinkClass} onClick={closeMobileMenu}>Advisory Board</Link>
                      <Link to="/contatti" className={mobileLinkClass} onClick={closeMobileMenu}>Contatti</Link>
                      <Link to="/login" onClick={closeMobileMenu}>
                        <Button variant="outline" className="border-2 border-black hover:bg-black hover:text-white transition-colors w-full mt-4">
                          Login
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard" className={mobileLinkClass} onClick={closeMobileMenu}>Dashboard</Link>
                      <Link to="/members" className={mobileLinkClass} onClick={closeMobileMenu}>Membri</Link>
                      <Link to="/member-news" className={mobileLinkClass} onClick={closeMobileMenu}>Bacheca</Link>
                      <Link to="/jobs" className={mobileLinkClass} onClick={closeMobileMenu}>Opportunità</Link>
                      <Link to="/activities" className={mobileLinkClass} onClick={closeMobileMenu}>Attività</Link>
                      <Link to="/archive" className={mobileLinkClass} onClick={closeMobileMenu}>Archivio</Link>
                      <Link to="/profile" className={mobileLinkClass} onClick={closeMobileMenu}>Profilo</Link>
                      
                      {/* Admin Menu Mobile */}
                      {hasAdminAccess && (
                        <div className="border-t border-gray-200 mt-3 pt-3">
                          <div className="text-sm font-semibold text-gray-500 mb-2 px-3">ADMIN</div>
                          {permissions.includes('ADMIN_GESTIONE_PERMESSI') && (
                            <Link to="/admin/roles" className={mobileLinkClass} onClick={closeMobileMenu}>Gestione Ruoli</Link>
                          )}
                          {permissions.includes('ADMIN_CATEGORIE_SISTEMA') && (
                            <Link to="/configurations" className={mobileLinkClass} onClick={closeMobileMenu}>Configurazioni di Sistema</Link>
                          )}
                          {permissions.includes('ADMIN_PAGAMENTI') && (
                            <Link to="/admin/payments" className={mobileLinkClass} onClick={closeMobileMenu}>Pagamenti Stripe</Link>
                          )}
                          {permissions.includes('ADMIN_SITUAZIONE_QUOTE') && (
                            <Link to="/admin/quote-situation" className={mobileLinkClass} onClick={closeMobileMenu}>Situazione quote</Link>
                          )}
                          {permissions.includes('ADMIN_ULTIMI_ACCESSI') && (
                            <Link to="/admin/recent-accesses" className={mobileLinkClass} onClick={closeMobileMenu}>Lista ultimi accessi</Link>
                          )}
                          {permissions.includes('LETTURA_ESITO_VOTAZIONE') && (
                            <Link to="/admin/voting-results" className={mobileLinkClass} onClick={closeMobileMenu}>Esiti Votazione</Link>
                          )}
                        </div>
                      )}
                      
                      <Button
                        variant="outline"
                        onClick={handleLogout}
                        className="border-2 border-black hover:bg-black hover:text-white transition-colors w-full mt-4"
                      >
                        Logout
                      </Button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
